import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { useSDUIFilteredComponentsRequest } from "@faire/retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Fetching/useSDUIFilteredComponentsRequest";
import { useSDUIFilterablePaginatedSectionState } from "@faire/retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Filters/useSDUIFilterablePaginatedSectionState";
import { useSettingNextJSRscCategoryPage } from "@faire/retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { useRefFrom } from "@faire/web--source/ui/hooks/useRefFrom";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { QueryParameters as FilteredComponentsQueryParams } from "@faire/web-api--source/api/v2/layout/filtered-components/post";
import { useQuery as useFilteredComponentsQuery } from "@faire/web-api--source/api/v2/layout/filtered-components/post-hooks";
import { IGetSDUIFilteredComponentsRequest } from "@faire/web-api--source/indigofair/data/IGetSDUIFilteredComponentsRequest";
import { IPaginationData } from "@faire/web-api--source/indigofair/data/IPaginationData";
import { keepPreviousData } from "@tanstack/react-query";
import React, { startTransition, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
export var SDUIFilterablePaginatedSectionNextPagePrefetcher = function(param) {
    var filterablePaginatedSection = param.filterablePaginatedSection, paginationData = param.paginationData, handlePaginationLink = param.handlePaginationLink;
    // TODO (Nancy Ngo): Prefetch RSC payload for next page when filterablePaginatedSection is undefined
    if (!filterablePaginatedSection) {
        return null;
    }
    return /*#__PURE__*/ _jsx(SDUIFilterablePaginatedSectionNextPagePrefetcherWithReactQueryOrRSC, {
        filterablePaginatedSection: filterablePaginatedSection,
        paginationData: paginationData,
        handlePaginationLink: handlePaginationLink
    });
};
var SDUIFilterablePaginatedSectionNextPagePrefetcherWithReactQueryOrRSC = function(param) {
    var filterablePaginatedSection = param.filterablePaginatedSection, paginationData = param.paginationData, handlePaginationLink = param.handlePaginationLink;
    var _SDUIFilteredComponentsRequest_pagination_data;
    var SDUIFilteredComponentsRequest = useSDUIFilteredComponentsRequest(filterablePaginatedSection);
    var _useLocation = useLocation(), pathname = _useLocation.pathname, search = _useLocation.search;
    var currentPath = "".concat(pathname).concat(search);
    var _React_useState = _sliced_to_array(React.useState(currentPath), 2), prefetchPagePath = _React_useState[0], setPrefetchPagePath = _React_useState[1];
    var _SDUIFilteredComponentsRequest_pagination_data_page_size;
    var pageSize = (_SDUIFilteredComponentsRequest_pagination_data_page_size = (_SDUIFilteredComponentsRequest_pagination_data = SDUIFilteredComponentsRequest.pagination_data) === null || _SDUIFilteredComponentsRequest_pagination_data === void 0 ? void 0 : _SDUIFilteredComponentsRequest_pagination_data.page_size) !== null && _SDUIFilteredComponentsRequest_pagination_data_page_size !== void 0 ? _SDUIFilteredComponentsRequest_pagination_data_page_size : 0;
    var _paginationData_page_count;
    var pageCount = (_paginationData_page_count = paginationData === null || paginationData === void 0 ? void 0 : paginationData.page_count) !== null && _paginationData_page_count !== void 0 ? _paginationData_page_count : 0;
    var oneIndexedPageNumber = useSDUIFilterablePaginatedSectionState().oneIndexedPageNumber;
    // give time for page to scroll to top after page change
    var debouncedSetPrefetchPagePath = useRefFrom(function() {
        return debounceIfPossible(function(path) {
            startTransition(function() {
                setPrefetchPagePath(path);
            });
        }, 1000);
    }).current;
    useEffect(function() {
        if (prefetchPagePath !== currentPath) {
            debouncedSetPrefetchPagePath(currentPath);
        }
    }, [
        prefetchPagePath,
        currentPath,
        debouncedSetPrefetchPagePath
    ]);
    var nextPageNumber = oneIndexedPageNumber + 1;
    var isNextPageAvailable = nextPageNumber <= pageCount && nextPageNumber > 0;
    var prefetchNextPageRequestPayload = useMemo(function() {
        return IGetSDUIFilteredComponentsRequest.build(_object_spread_props(_object_spread({}, SDUIFilteredComponentsRequest), {
            pagination_data: IPaginationData.build({
                page_size: pageSize,
                page_number: nextPageNumber
            })
        }));
    }, [
        SDUIFilteredComponentsRequest,
        nextPageNumber,
        pageSize
    ]);
    if (!isNextPageAvailable || prefetchPagePath !== currentPath) {
        return null;
    }
    return /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
        useTransition: true,
        threshold: 0,
        children: /*#__PURE__*/ _jsx(FilteredComponentsPrefetcher, {
            prefetchNextPageRequestPayload: prefetchNextPageRequestPayload,
            prefetchedPageLink: handlePaginationLink(nextPageNumber)
        })
    });
};
var FilteredComponentsPrefetcher = function(param) {
    var prefetchNextPageRequestPayload = param.prefetchNextPageRequestPayload, prefetchedPageLink = param.prefetchedPageLink;
    var _useNextRouterContext = useNextRouterContext(), nextPrefetch = _useNextRouterContext.prefetch;
    var isCategoryRSCEnabled = useSettingNextJSRscCategoryPage();
    useFilteredComponentsQuery(prefetchNextPageRequestPayload, FilteredComponentsQueryParams.build({}), {
        staleTime: Infinity,
        placeholderData: keepPreviousData,
        enabled: !isCategoryRSCEnabled
    });
    if (isCategoryRSCEnabled) {
        nextPrefetch(prefetchedPageLink);
    }
    return null;
};
